import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import DateTimePicker from "../../components/DateTimePicker";
import * as adminApi from "../../api/adminApi";
import { useToast } from "../../context/ToastContext";
import { formatDate, formatMoney, formatOrderStatus, getCanonicalOrderStatus } from "../../utils/format";
import "./AdminOrders.css";

const STATUS_FILTER = [
  { value: "", label: "All statuses" },
  { value: "Pending", label: "Pending" },
  { value: "Confirmed", label: "Confirmed" },
  { value: "Processing", label: "Processing" },
  { value: "Shipping", label: "Shipped" },
  { value: "Completed", label: "Delivered" },
  { value: "Cancelled", label: "Cancelled" },
];

function statusTone(s) {
  const canonical = getCanonicalOrderStatus(s);
  if (canonical === "Completed") return "delivered";
  if (canonical === "Shipping") return "shipped";
  if (canonical === "Cancelled" || canonical === "Returned" || canonical === "Expired") return "bad";
  if (canonical === "Confirmed" || canonical === "Processing") return "good";
  return "neutral";
}

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const statusFilter = searchParams.get("status") || "";

  const userIdFilter = searchParams.get("userId") || "";
  const fromDateFilter = searchParams.get("fromDate") || "";
  const toDateFilter = searchParams.get("toDate") || "";

  const { addToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [isSearchingOrder, setIsSearchingOrder] = useState(false);

  // Email search state
  const [emailSearch, setEmailSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .fetchAdminOrders({ 
        page, 
        pageSize: 12, 
        status: statusFilter || undefined, 
        userId: userIdFilter || undefined,
        fromDate: fromDateFilter || undefined,
        toDate: toDateFilter || undefined
      })
      .then((data) => {
        if (!cancelled) setOrders(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((e) => {
        if (!cancelled) addToast(e.message || "Failed to load orders.", "error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter, userIdFilter, fromDateFilter, toDateFilter]);

  // Debounced search effect
  useEffect(() => {
    if (!emailSearch) {
      setUserOptions([]);
      return;
    }
    
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    searchDebounceRef.current = setTimeout(() => {
      adminApi.searchAdminUsers(emailSearch)
        .then((data) => {
          if (data && data.items) {
            setUserOptions(data.items);
            setShowDropdown(true);
          }
        })
        .catch(() => {});
    }, 300);
    
    return () => clearTimeout(searchDebounceRef.current);
  }, [emailSearch]);
  const handleSearchOrder = async (e) => {
    e.preventDefault();
    if (!orderIdSearch.trim()) return;
    setIsSearchingOrder(true);
    try {
      const res = await adminApi.fetchAdminOrder(orderIdSearch.trim());
      if (res && res.id) {
        navigate(`/admin/orders/${res.id}`);
      }
    } catch (err) {
      addToast("Order not found or invalid ID.", "error");
    } finally {
      setIsSearchingOrder(false);
    }
  };

  const handleSelectUser = (u) => {
    setSearchParams({ page: "1", userId: u.id, ...(statusFilter ? { status: statusFilter } : {}) });
    setEmailSearch("");
    setShowDropdown(false);
  };

  const clearUserFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("userId");
    params.set("page", "1");
    setSearchParams(params);
  };


  return (
    <div className="admin-orders">
      <div className="admin-orders__head">
        <div>
          <p className="section-label">Fulfillment</p>
          <h2 className="admin-orders__title">Orders</h2>
        </div>
        <div className="admin-orders__filters">
          <div className="admin-user-search">
            {userIdFilter ? (
              <div className="admin-user-search__active">
                <span className="admin-badge admin-badge--neutral">User ID: {userIdFilter.slice(0, 8)}...</span>
                <button type="button" onClick={clearUserFilter} className="admin-btn admin-btn--cancel">Clear</button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Search user by email..."
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  onFocus={() => { if (userOptions.length > 0) setShowDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                  className="admin-input"
                  style={{ width: "250px" }}
                />
                {showDropdown && userOptions.length > 0 && (
                  <div className="admin-user-search__dropdown">
                    {userOptions.map((u) => (
                      <div
                        key={u.id}
                        className="admin-user-search__option"
                        onClick={() => handleSelectUser(u)}
                      >
                        {u.email}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          <form className="admin-order-id-search" onSubmit={handleSearchOrder} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="Search by Order ID..."
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              className="admin-input"
              style={{ width: "200px" }}
            />
            <button 
              type="submit" 
              className="admin-btn admin-btn--primary"
              disabled={isSearchingOrder || !orderIdSearch.trim()}
              style={{ padding: "8px 14px", height: "35px", display: "flex", alignItems: "center" }}
            >
              {isSearchingOrder ? "..." : "Go"}
            </button>
          </form>

          <div className="admin-date-filter">
            <div className="admin-date-range-box">
              <label className="admin-date-range-label">Start Range</label>
              <DateTimePicker
                value={fromDateFilter}
                onChange={(val) => {
                  const params = new URLSearchParams(searchParams);
                  params.set("page", "1");
                  if (val) params.set("fromDate", val);
                  else params.delete("fromDate");
                  setSearchParams(params);
                }}
              />
            </div>
            <div className="admin-date-range-box">
              <label className="admin-date-range-label">End Range</label>
              <DateTimePicker
                value={toDateFilter}
                onChange={(val) => {
                  const params = new URLSearchParams(searchParams);
                  params.set("page", "1");
                  if (val) params.set("toDate", val);
                  else params.delete("toDate");
                  setSearchParams(params);
                }}
              />
            </div>
            {(fromDateFilter || toDateFilter) && (
              <button 
                type="button" 
                className="admin-btn admin-btn--cancel admin-date-clear" 
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.delete("fromDate");
                  params.delete("toDate");
                  params.set("page", "1");
                  setSearchParams(params);
                }}
              >
                Clear
              </button>
            )}
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              const v = e.target.value;
              const params = new URLSearchParams(searchParams);
              params.set("page", "1");
              if (v) params.set("status", v);
              else params.delete("status");
              setSearchParams(params);
            }}
            className="admin-select"
          >
            {STATUS_FILTER.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>Status</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr>
                  <td colSpan={4} className="admin-table__empty">
                    No orders match this filter.
                  </td>
                </tr>
              )}
              {orders.map((o) => {
                const label = formatOrderStatus(o.orderStatus);
                const tone = statusTone(o.orderStatus);
                return (
                  <tr
                    key={o.id}
                    className="admin-orders-row--clickable"
                    onClick={() => navigate(`/admin/orders/${o.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/admin/orders/${o.id}`);
                      }
                    }}
                  >
                    <td>
                      <Link
                        to={`/admin/orders/${o.id}`}
                        className="admin-order-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        #{String(o.id).slice(0, 8)}
                      </Link>
                    </td>
                    <td>{formatDate(o.createdAt)}</td>
                    <td>
                      <span className={`admin-badge admin-badge--${tone}`}>{label}</span>
                    </td>
                    <td>{formatMoney(o.totalPrice)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-pager">
        <button
          type="button"
          className="btn-ghost"
          disabled={page <= 1}
          onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: String(page - 1) })}
        >
          Previous
        </button>
        <span>Page {page}</span>
        <button
          type="button"
          className="btn-ghost"
          disabled={orders.length < 12}
          onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: String(page + 1) })}
        >
          Next
        </button>
      </div>
    </div>
  );
}
